import React, { useMemo } from "react";
import {
  DataGrid,
  GridSortDirection,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { Box, Chip, IconButton, styled, Tooltip, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import moment from "moment";
import { EndUser } from "../../../../Types";

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .no-rows-primary": {
    fill: "#3D4751",
    ...theme.applyStyles("light", {
      fill: "#AEB8C2",
    }),
  },
  "& .no-rows-secondary": {
    fill: "#1D2126",
    ...theme.applyStyles("light", {
      fill: "#E8EAED",
    }),
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        width="120"
        height="100"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false"
      >
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse
              className="no-rows-primary"
              cx="67.797"
              cy="106.89"
              rx="67.797"
              ry="12.668"
            />
            <path
              className="no-rows-primary"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="no-rows-secondary"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="no-rows-primary"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM61.355 48.088h9.168a2 2 0 0 1 2 2v9.168a2 2 0 0 1-2 2h-9.168a2 2 0 0 1-2-2v-9.168a2 2 0 0 1 2-2z"
            />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1, color: "text.secondary" }}>
        <Typography variant="body2">No end users found</Typography>
      </Box>
    </StyledGridOverlay>
  );
}

type EndUsersToolbarProps = {
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
};

const EndUsersToolbar: React.FC<EndUsersToolbarProps> = ({
  onOpenAdvancedFilter,
  hasActiveFilter,
}) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter debounceMs={500} />
    {onOpenAdvancedFilter && (
      <Tooltip title="Advanced Filter">
        <IconButton
          onClick={onOpenAdvancedFilter}
          color={hasActiveFilter ? "primary" : "default"}
          data-testid="FilterListIcon"
          size="small"
          sx={{ ml: 1 }}
          aria-label="Advanced Filter"
        >
          <FilterListIcon />
          <Typography variant="body2" sx={{ ml: 0.5, display: { xs: "none", sm: "inline-flex" } }}>
            Advanced Filter
          </Typography>
        </IconButton>
      </Tooltip>
    )}
  </GridToolbarContainer>
);

interface EndUsersTableProps {
  loading: boolean;
  filteredData: EndUser[] | [];
  page?: number;
  pageSize?: number;
  rowCount?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  sortModel?: { field: string; sort: GridSortDirection }[];
  onSortModelChange?: (newModel: any) => void;
  onFilterModelChange?: (model: any) => void;
  searchText?: string;
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
}

const EndUsersTable = ({
  loading,
  filteredData,
  page = 0,
  pageSize = 20,
  rowCount = 0,
  onPageChange,
  onPageSizeChange,
  sortModel = [],
  onSortModelChange,
  onFilterModelChange,
  searchText = "",
  onOpenAdvancedFilter,
  hasActiveFilter = false,
}: EndUsersTableProps) => {
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 150,
        renderCell: (params: any) => {
          return (
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {params.row.name}
            </Typography>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        minWidth: 200,
      },
      // Hidden columns - can be re-enabled later
      // {
      //   field: "contact",
      //   headerName: "Contact",
      //   flex: 1,
      //   minWidth: 130,
      //   valueGetter: (params: any) => params.row.contact || "N/A",
      // },
      // {
      //   field: "ownershipType",
      //   headerName: "Ownership Type",
      //   flex: 1,
      //   minWidth: 150,
      //   renderCell: (params: any) => {
      //     const type = getOwnershipTypeLabel(params.row);
      //     let color: "primary" | "secondary" | "success" | "warning" | "error" | "info" = "info";
      //     
      //     if (type === "MASJID") color = "primary";
      //     else if (type === "ORGANIZATION") color = "secondary";
      //     else if (type === "INDIVIDUAL") color = "success";

      //     return <Chip label={type} color={color} size="small" />;
      //   },
      // },
      // {
      //   field: "entities",
      //   headerName: "Associated Entities",
      //   flex: 2,
      //   minWidth: 200,
      //   sortable: false,
      //   valueGetter: (params: any) => getEntityDetails(params.row),
      //   renderCell: (params: any) => {
      //     const details = getEntityDetails(params.row);
      //     return (
      //       <Typography 
      //         variant="body2" 
      //         style={{ 
      //           whiteSpace: "nowrap", 
      //           overflow: "hidden", 
      //           textOverflow: "ellipsis" 
      //         }}
      //         title={details}
      //       >
      //         {details}
      //       </Typography>
      //     );
      //   },
      // },
      // {
      //   field: "isVerified",
      //   headerName: "Verified",
      //   flex: 0.8,
      //   minWidth: 100,
      //   renderCell: (params: any) => {
      //     return (
      //       <Chip
      //         label={params.row.isVerified ? "Yes" : "No"}
      //         color={params.row.isVerified ? "success" : "default"}
      //         size="small"
      //       />
      //     );
      //   },
      // },
      // {
      //   field: "isApproved",
      //   headerName: "Approved",
      //   flex: 0.8,
      //   minWidth: 100,
      //   renderCell: (params: any) => {
      //     return (
      //       <Chip
      //         label={params.row.isApproved ? "Yes" : "No"}
      //         color={params.row.isApproved ? "success" : "warning"}
      //         size="small"
      //       />
      //     );
      //   },
      // },
      {
        field: "isFreezed",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: (params: any) => {
          const isFreezed = params.row.isFreezed;
          return (
            <Chip
              label={isFreezed ? "Frozen" : "Active"}
              color={isFreezed ? "error" : "success"}
              size="small"
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        flex: 1.2,
        minWidth: 160,
        valueGetter: (params: any) => {
          const value = params.row.createdAt;
          return value
            ? moment.utc(value).local().format("DD-MMM-YYYY HH:mm")
            : "-";
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1.2,
        minWidth: 160,
        valueGetter: (params: any) => {
          const value = params.row.updatedAt;
          return value
            ? moment.utc(value).local().format("DD-MMM-YYYY HH:mm")
            : "-";
        },
      },
    ],
    []
  );

  return (
    <>
      <div
        style={{
          height: "100%",
          minHeight: loading ? 650 : undefined,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <DataGrid
          style={{ backgroundColor: "white" }}
          loading={loading}
          rows={filteredData}
          columns={columns}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          filterMode="server"
          onFilterModelChange={onFilterModelChange}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          components={{
            Toolbar: EndUsersToolbar,
            NoRowsOverlay: CustomNoRowsOverlay,
          }}
          componentsProps={{
            toolbar: {
              onOpenAdvancedFilter,
              hasActiveFilter,
            },
          }}
          disableSelectionOnClick
          getRowId={(row) => row._id}
          getRowClassName={(params) => {
            return params.row?.isFreezed ? "frozen-user-row" : "";
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
            "& .frozen-user-row": {
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffcdd2",
              },
            },
          }}
        />
      </div>
    </>
  );
};

export default EndUsersTable; 